import React, { useCallback, useEffect, useState } from 'react';
import { ClinicLocation, ClinicLocationService } from '../services/clinicLocationService';

export const ClinicLocationsManagerPanel: React.FC = () => {
  const [clinics, setClinics] = useState<ClinicLocation[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Partial<ClinicLocation>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setClinics(await ClinicLocationService.listClinics(true));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load clinics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async (clinicId: number) => {
    setSaving(true);
    setError(null);
    try {
      await ClinicLocationService.updateClinic(clinicId, {
        name: draft.name,
        city: draft.city,
        lat: draft.lat,
        lng: draft.lng,
        active: draft.active,
        address: draft.address ?? undefined,
        practice_location_id: draft.practice_location_id ?? undefined,
      });
      setEditingId(null);
      setDraft({});
      setSavedId(clinicId);
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save clinic. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="referrer-type-panel loading">Loading clinic locations…</div>;
  }

  return (
    <div className="referrer-type-panel">
      {error && <div className="error-banner">{error}</div>}

      <section className="referrer-type-section skill-enum-section">
        <div className="section-heading-row">
          <h3>Clinic routing enum</h3>
          <span className="tier-score">Haversine routing</span>
        </div>
        <p className="section-help">
          Active clinics are eligible for nearest-clinic assignment. Inactive clinics are excluded from routing.
        </p>

        <div className="referrer-type-table-wrap">
          <table className="referrer-type-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>City</th>
                <th>Lat</th>
                <th>Lng</th>
                <th>Active</th>
                <th>Address</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {clinics.map((clinic) => {
                const editing = editingId === clinic.clinic_id;
                return (
                  <tr key={clinic.clinic_id}>
                    <td>
                      {editing ? (
                        <input
                          value={draft.name ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                        />
                      ) : (
                        clinic.name
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={draft.city ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...d, city: e.target.value }))}
                        />
                      ) : (
                        clinic.city
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          type="number"
                          step="0.000001"
                          value={draft.lat ?? clinic.lat}
                          onChange={(e) => setDraft((d) => ({ ...d, lat: Number(e.target.value) }))}
                          className="input-narrow"
                        />
                      ) : (
                        clinic.lat
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          type="number"
                          step="0.000001"
                          value={draft.lng ?? clinic.lng}
                          onChange={(e) => setDraft((d) => ({ ...d, lng: Number(e.target.value) }))}
                          className="input-narrow"
                        />
                      ) : (
                        clinic.lng
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          type="checkbox"
                          checked={draft.active ?? clinic.active}
                          onChange={(e) => setDraft((d) => ({ ...d, active: e.target.checked }))}
                        />
                      ) : (
                        clinic.active ? 'Yes' : 'No'
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <input
                          value={draft.address ?? clinic.address ?? ''}
                          onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
                        />
                      ) : (
                        clinic.address || '—'
                      )}
                    </td>
                    <td>
                      {editing ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-link"
                            onClick={() => handleSave(clinic.clinic_id)}
                            disabled={saving}
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-link"
                            disabled={saving}
                            onClick={() => {
                              setEditingId(null);
                              setDraft({});
                              setError(null);
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <span className="clinic-row-actions">
                          <button
                            type="button"
                            className="btn btn-link"
                            onClick={() => {
                              setEditingId(clinic.clinic_id);
                              setSavedId(null);
                              setDraft({ ...clinic });
                            }}
                          >
                            Edit
                          </button>
                          {savedId === clinic.clinic_id && (
                            <span className="save-success-indicator">Saved</span>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
